import { useCallback, useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import VehicleModal from '../components/VehicleModal';
import BulkImportModal from '../components/BulkImportModal';
import { vehiclesService, campaignsService, quotesService } from '../lib/services';
import { useToast } from '../lib/toast';
import { formatDateTime } from '../lib/datetime';

const VEHICLE_IMPORT_COLUMNS = [
  { key: 'make', required: true }, { key: 'model', required: true }, { key: 'trim' }, { key: 'year' },
  { key: 'vin' }, { key: 'stockId' }, { key: 'body' }, { key: 'fuel' }, { key: 'transmission' },
  { key: 'km' }, { key: 'condition' }, { key: 'driveaway' }, { key: 'rrp' }, { key: 'cost' },
  { key: 'colour' }, { key: 'colourInterior' }, { key: 'drivetrain' }, { key: 'engine' },
  { key: 'engineCc' }, { key: 'cylinders' }, { key: 'powerKw' }, { key: 'doors' }, { key: 'seats' },
  { key: 'rego' }, { key: 'regoExpiry' }, { key: 'buildDate' }, { key: 'complianceDate' },
  { key: 'features' }, { key: 'description' }, { key: 'status' }, { key: 'primaryPhoto' }, { key: 'photos' },
  { key: 'acquired' },   // arrival date (YYYY-MM-DD) — drives days-in-stock and the aged filter
];
const VEHICLE_SAMPLE_ROW = [
  'Toyota', 'RAV4', 'GXL', '2024', '', 'STK1001', 'SUV', 'Hybrid', 'Automatic',
  '0', 'NEW', '58990', '62500', '52000', 'Eclipse Black', 'Charcoal', 'AWD', '2.5L Hybrid 4cyl',
  '2487', '4', '131', '5', '5', '', '', '2024-01', '2024-02',
  'Apple CarPlay; Adaptive cruise', 'One owner, full service history', 'IN_STOCK',
  'https://example.com/a.jpg', 'https://example.com/a.jpg; https://example.com/b.jpg',
  '2026-03-14',
];

const STATUS_OPTIONS = ['IN_STOCK', 'RESERVED', 'SOLD', 'IN_TRANSIT', 'ARCHIVED'];

const STATUS_LABEL = {
  IN_STOCK: 'In stock',
  RESERVED: 'Reserved',
  SOLD: 'Sold',
  IN_TRANSIT: 'In transit',
  ARCHIVED: 'Archived',
};

const STATUS_TONE = {
  IN_STOCK: 'success',
  RESERVED: 'warning',
  SOLD: 'danger',
  IN_TRANSIT: 'accent',
  ARCHIVED: '',
};

const CONDITION_OPTIONS = ['NEW', 'DEMO', 'USED'];

function fmtPriceFromCents(c) {
  if (c == null) return '—';
  return '$' + Math.round(c / 100).toLocaleString('en-AU');
}

function fmtKm(km) {
  if (km == null) return '—';
  return km.toLocaleString('en-AU') + ' km';
}

const AGED_DAYS = 60;      // amber past this
const STALE_DAYS = 90;     // red past this

function daysTone(days) {
  if (days == null) return '';
  if (days >= STALE_DAYS) return 'danger';
  if (days >= AGED_DAYS) return 'warning';
  return '';
}
function fmtDays(days) {
  if (days == null) return '—';
  return days === 1 ? '1 day' : `${days} days`;
}

const PAGE_SIZE = 10;   // vehicles per page (server-side)

function unwrapPage(resp) {
  // The list endpoint returns ApiResponse<PageResponse<Vehicle>>.
  const pageObj = resp?.data ?? resp ?? {};
  const content = pageObj.content
    ?? (Array.isArray(resp?.data) ? resp.data : null)
    ?? (Array.isArray(resp) ? resp : [])
    ?? [];
  return {
    content,
    page: pageObj.page ?? 0,
    totalElements: pageObj.totalElements ?? content.length,
    totalPages: pageObj.totalPages ?? 1,
    last: pageObj.last ?? true,
    first: pageObj.first ?? true,
  };
}

export default function Inventory() {
  const { toast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Server-side pagination
  const [page, setPage] = useState(0);          // 0-based
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Facets from the backend (dealership-wide, independent of the current page)
  const [stats, setStats] = useState({
    total: 0, inStock: 0, reserved: 0, inTransit: 0, sold: 0, archived: 0, aged: 0,
    agedThresholdDays: AGED_DAYS,
  });
  const [makes, setMakes] = useState([]);

  // Filters
  const [search, setSearch] = useState('');       // raw input
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [makeFilter, setMakeFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agedOnly, setAgedOnly] = useState(false);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detailVehicle, setDetailVehicle] = useState(null);   // read-only detail view

  // Debounce the free-text search; resetting to page 0 on every new query.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        all_statuses: true,   // admin screen sees every status, not just IN_STOCK
      };
      if (debouncedSearch) params.q = debouncedSearch;
      if (makeFilter) params.make = makeFilter;
      if (conditionFilter) params.condition = conditionFilter;
      if (statusFilter) params.stock_status = statusFilter;
      if (agedOnly) params.aged_days = stats.agedThresholdDays || AGED_DAYS;

      const resp = await vehiclesService.list(params);
      const p = unwrapPage(resp);
      setRows(p.content);
      setTotalElements(p.totalElements);
      setTotalPages(p.totalPages);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load inventory');
      setRows([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, makeFilter, conditionFilter, statusFilter, agedOnly, stats.agedThresholdDays]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- async loader; setState runs post-await
  useEffect(() => { load(); }, [load]);

  // Facets: dealership-wide KPI counts + the full makes list for the dropdown.
  const loadFacets = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        vehiclesService.stats(),
        vehiclesService.makes(),
      ]);
      if (s) setStats({
        total: s.total ?? 0, inStock: s.inStock ?? 0,
        reserved: s.reserved ?? 0, aged: s.aged ?? 0,
        inTransit: s.inTransit ?? 0, sold: s.sold ?? 0, archived: s.archived ?? 0,
        agedThresholdDays: s.agedThresholdDays ?? AGED_DAYS,
      });
      if (Array.isArray(m)) setMakes(m);
    } catch {
      /* KPI/makes are non-critical; leave prior values on failure */
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- async loader; setState runs post-await
  useEffect(() => { loadFacets(); }, [loadFacets]);

  // Filter change handlers reset to the first page so results stay coherent.
  const onMakeFilter      = (v) => { setMakeFilter(v);      setPage(0); };
  const onConditionFilter = (v) => { setConditionFilter(v); setPage(0); };
  const onStatusFilter    = (v) => { setStatusFilter(v);    setPage(0); };
  const toggleAged        = ()  => { setAgedOnly(a => !a);  setPage(0); };
  const clearFilters = () => {
    setSearch(''); setDebouncedSearch('');
    setMakeFilter(''); setConditionFilter(''); setStatusFilter(''); setAgedOnly(false);
    setPage(0);
  };

  const hasFilters = !!(search || makeFilter || conditionFilter || statusFilter || agedOnly);

  // Dealer-configurable aged-stock threshold (one source of truth, server-stored).
  const editThreshold = async () => {
    const current = stats.agedThresholdDays || AGED_DAYS;
    const raw = window.prompt('Aged-stock threshold (days) — stock older than this counts as aged:', String(current));
    if (raw == null) return;
    const days = parseInt(raw, 10);
    if (!Number.isFinite(days) || days < 1 || days > 3650) {
      toast('Enter a number of days between 1 and 3650', { variant: 'error' });
      return;
    }
    try {
      await vehiclesService.setAgedThreshold(days);
      setStats(s => ({ ...s, agedThresholdDays: days }));
      toast(`Aged-stock threshold set to ${days} days`, { variant: 'success' });
      loadFacets();
    } catch (e) {
      toast(`Could not update threshold: ${e?.message || 'unknown error'}`, { variant: 'error' });
    }
  };

  // #4 — turn an aged unit into a targeted draft campaign (customer-matching).
  // Composed client-side against the standard campaigns API so it works against
  // any backend that has the (long-standing) /v1/campaigns endpoint. The
  // vehicleModel segment criterion targets customers whose enquiry names this
  // model; review the audience before launching.
  const createCampaign = async (v) => {
    const label = [v.year, v.make, v.model].filter(Boolean).join(' ');
    const price = v.driveAwayPriceCents != null
      ? ` from $${Math.round(v.driveAwayPriceCents / 100).toLocaleString('en-AU')} driveaway` : '';
    const body = {
      name: `Aged stock — ${label}`,
      description: `Auto-created from an aged-stock alert`
        + (v.daysInStock != null ? ` (${v.daysInStock} days on lot)` : '')
        + `. Targets customers whose enquiry matches ${v.model}.`,
      type: 'ONE_OFF',
      segmentQuery: { vehicleModel: v.model },
      steps: [{
        stepOrder: 0,
        offsetDays: 0,
        channel: 'SMS',
        bodyOverride: `Hi {{firstName}}, we still have the ${label} available${price}`
          + ` — keen to arrange a quick test drive? Reply YES and we'll lock in a time.`,
      }],
    };
    try {
      const c = await campaignsService.create(body);
      toast(`Draft campaign created: ${c?.name || label} — review & launch it in Marketing`,
        { variant: 'success' });
    } catch (e) {
      toast(`Could not create campaign: ${e?.response?.data?.message || e?.message || 'unknown error'}`,
        { variant: 'error' });
    }
  };

  const openCreate = () => {
    setActiveVehicle(null);
    setModalMode('create');
    setModalOpen(true);
  };
  const openEdit = (v) => {
    setActiveVehicle(v);
    setModalMode('edit');
    setModalOpen(true);
  };

  const onSave = async (payload) => {
    try {
      if (modalMode === 'edit' && activeVehicle?.id) {
        const saved = await vehiclesService.update(activeVehicle.id, payload);
        setRows(r => r.map(x => x.id === saved.id ? saved : x));
        toast(`Updated ${saved.make} ${saved.model}`, { variant: 'success' });
        loadFacets();   // status/price may have changed → refresh KPIs
      } else {
        const saved = await vehiclesService.create(payload);
        toast(`Added ${saved.make} ${saved.model}`, { variant: 'success' });
        // New row lives on the server now — jump to the first page and reload
        // so paging/counts stay accurate rather than splicing locally.
        setPage(0);
        load();
        loadFacets();
      }
      setModalOpen(false);
    } catch (e) {
      toast(`Save failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'error' });
    }
  };

  const changeStatus = async (v, next) => {
    const prev = v.stockStatus;
    // High-impact changes are one mis-click away in an inline dropdown; ask first.
    // (The dropdown is controlled, so declining leaves the row as it was.)
    if (next === 'SOLD' || (prev === 'RESERVED' && next === 'IN_STOCK')) {
      const what = v.dmsStockId ? `${v.make} ${v.model} (${v.dmsStockId})` : `${v.make} ${v.model}`;
      const msg = next === 'SOLD'
        ? `Mark ${what} as SOLD? It will no longer be available for any deal.`
        : `Release the reservation on ${what} and put it back In stock?`;
      if (!window.confirm(msg)) return;
    }
    // Optimistic.
    setRows(r => r.map(x => x.id === v.id ? { ...x, stockStatus: next } : x));
    try {
      const saved = await vehiclesService.setStatus(v.id, next);
      setRows(r => r.map(x => x.id === v.id ? saved : x));
      toast(`${v.make} ${v.model} → ${STATUS_LABEL[next]}`, { variant: 'success' });
      loadFacets();   // KPI counts shifted between statuses
      // If a status filter is active, the row may no longer belong here.
      if (statusFilter) load();
    } catch (e) {
      // Roll back.
      setRows(r => r.map(x => x.id === v.id ? { ...x, stockStatus: prev } : x));
      toast(`Status change failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <>
      <TopBar
        crumbs={['Inventory']}
        right={
          <>
            <button className="btn small" onClick={load} title="Refresh from the synced stock store">↻ Refresh</button>
            <button className="btn small" onClick={() => setImportOpen(true)}>⭱ Import</button>
            <button className="btn small primary" onClick={openCreate}>+ Add vehicle</button>
          </>
        }
      />

      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* KPI strip */}
        <div className="kpi-strip" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
          <KpiCard label="Total vehicles"  value={stats.total}     tone="" />
          <KpiCard label="In stock"        value={stats.inStock}   tone="success" />
          <KpiCard label="Reserved"        value={stats.reserved}  tone="warning" />
          <KpiCard label="In transit"      value={stats.inTransit} tone="accent" />
          <KpiCard label="Sold"            value={stats.sold}      tone="danger" />
          <KpiCard label="Archived"        value={stats.archived}  tone="" />
          <KpiCard label={`Aged (${stats.agedThresholdDays}d+)`} value={stats.aged} tone="danger" />
        </div>

        {/* Toolbar */}
        <div className="crm-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="crm-search"
            placeholder="Search by make, model, VIN, stock #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: '1 1 280px', minWidth: 220 }}
          />
          <select className="btn small" value={makeFilter} onChange={(e) => onMakeFilter(e.target.value)}>
            <option value="">All makes</option>
            {makes.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="btn small" value={conditionFilter} onChange={(e) => onConditionFilter(e.target.value)}>
            <option value="">All conditions</option>
            {CONDITION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="btn small" value={statusFilter} onChange={(e) => onStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <button
            className={`btn small ${agedOnly ? 'primary' : ''}`}
            onClick={toggleAged}
            aria-pressed={agedOnly}
            title={`Filter: show only unsold stock that has been on the lot ${stats.agedThresholdDays}+ days`}
          >{agedOnly ? '✓ ' : ''}Aged stock · {stats.agedThresholdDays}d+</button>
          <button
            className="btn small"
            onClick={editThreshold}
            title="Not a filter. Sets how many days make stock 'aged' (applies to the Aged stock filter and the Aged KPI). Click to change."
          >⚙ Aged threshold: {stats.agedThresholdDays}d</button>
          {hasFilters && (
            <button className="btn small" onClick={clearFilters}>Clear</button>
          )}
          {agedOnly && !loading && !loadError && (
            <span className="mono text-xs text-muted">
              {totalElements} unsold unit{totalElements === 1 ? '' : 's'} on the lot {stats.agedThresholdDays}+ days
            </span>
          )}
        </div>

        {/* Table */}
        <div className="card" style={{ padding: 0 }}>
          {loading && (
            <div className="empty-state" style={{ padding: 40 }}>
              <div className="icon">⏳</div>
              <div className="title">Loading inventory…</div>
            </div>
          )}
          {!loading && loadError && (
            <div className="empty-state" style={{ padding: 40 }}>
              <div className="icon">⚠</div>
              <div className="title">Couldn't load inventory</div>
              <div className="desc">{loadError}</div>
              <button className="btn small" style={{ marginTop: 12 }} onClick={load}>Try again</button>
            </div>
          )}
          {!loading && !loadError && rows.length === 0 && (
            <div className="empty-state" style={{ padding: 40 }}>
              <div className="icon">🚗</div>
              <div className="title">{hasFilters ? 'No vehicles match' : 'No vehicles yet'}</div>
              <div className="desc">
                {hasFilters
                  ? 'Try clearing your filters.'
                  : 'Inventory is empty — add a vehicle manually or wait for the next stock sync.'}
              </div>
              {hasFilters ? (
                <button type="button" className="btn small" style={{ marginTop: 8 }} onClick={clearFilters}>
                  Clear filters
                </button>
              ) : (
                <button type="button" className="btn small primary" style={{ marginTop: 8 }} onClick={openCreate}>
                  + New vehicle
                </button>
              )}
            </div>
          )}
          {!loading && !loadError && rows.length > 0 && (
            <div className="table-scroll">
              <table
                className="data-table"
                style={{ width: '100%', tableLayout: 'fixed' }}
              >
                {/*
                Fixed table layout + colgroup with explicit widths so the
                browser uses the SAME width for both header and body cells
                in every row. The Vehicle col has no width, which under
                `table-layout: fixed` means "take whatever space is left
                after the other columns" — no flex maths, no per-row
                drift, perfect alignment.
              */}
              <colgroup>
                <col style={{ width: 44 }}  />       {/* Photo */}
                <col />                              {/* Vehicle (flex) */}
                <col style={{ width: 54 }}  />       {/* Year */}
                <col style={{ width: 82 }}  />       {/* Condition */}
                <col style={{ width: 90 }}  />       {/* KM */}
                <col style={{ width: 98 }}  />       {/* Driveaway */}
                <col style={{ width: 80 }}  />       {/* Days in stock */}
                <col style={{ width: 100 }} />       {/* Stock # */}
                <col style={{ width: 124 }} />       {/* Status */}
                <col style={{ width: 104 }} />       {/* Actions (Edit + aged campaign) */}
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Vehicle</th>
                  <th>Year</th>
                  <th>Condition</th>
                  <th>KM</th>
                  <th>Driveaway</th>
                  <th>Days</th>
                  <th>Stock #</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(v => (
                  /*
                    No `crm-row` here — that class sets `display:grid` with
                    a hardcoded 8-column template meant for non-table card
                    lists, which overrides the table layout and the row
                    cells stop aligning with the thead colgroup. Plain
                    <tr> behaves like a normal table row.
                  */
                  <tr key={v.id}>
                    <td style={{ padding: 4 }}>
                      {v.primaryPhotoUrl ? (
                        <img
                          src={v.primaryPhotoUrl}
                          alt=""
                          loading="lazy"
                          style={{ width: 44, height: 32, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                        />
                      ) : (
                        <div style={{ width: 44, height: 32, borderRadius: 4, background: 'var(--surface-2, #f1f2f4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }} aria-hidden="true">🚗</div>
                      )}
                    </td>
                    <td>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setDetailVehicle(v)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setDetailVehicle(v); }}
                        style={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                        }}
                        title={`${v.make || ''} ${v.model || ''} — click for full details (VIN, registration, reservation)`.trim()}
                      >
                        {v.make} {v.model}
                      </div>
                      <div
                        className="mono text-xs text-muted"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {v.trim || '—'} · {v.colourExterior || v.body || '—'} · {v.fuel || '—'}
                      </div>
                    </td>
                    <td>{v.year ?? '—'}</td>
                    <td><span className="pill">{v.condition || '—'}</span></td>
                    <td>{fmtKm(v.km)}</td>
                    <td>{fmtPriceFromCents(v.driveAwayPriceCents)}</td>
                    <td>
                      {v.stockStatus === 'SOLD'
                        ? <span className="text-muted">—</span>
                        : <span className={`pill ${daysTone(v.daysInStock)} ${daysTone(v.daysInStock) ? 'filled' : ''}`}>{fmtDays(v.daysInStock)}</span>}
                    </td>
                    <td
                      className="mono text-xs text-muted"
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={v.dmsStockId || v.vin || ''}
                    >
                      {v.dmsStockId || v.vin || '—'}
                    </td>
                    <td>
                      <select
                        className={`pill ${STATUS_TONE[v.stockStatus] || ''} filled`}
                        style={{
                          border: 'none',
                          padding: '2px 8px',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                        value={v.stockStatus || 'IN_STOCK'}
                        onChange={(e) => changeStatus(v, e.target.value)}
                        aria-label="Change stock status"
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {/* Edit stays rightmost so it lines up on every row; the
                          campaign action is a compact icon shown only for aged
                          stock, sitting just to its left. */}
                      <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {v.stockStatus !== 'SOLD'
                          && v.daysInStock != null
                          && v.daysInStock >= (stats.agedThresholdDays || AGED_DAYS) && (
                          <button
                            className="btn small"
                            onClick={() => createCampaign(v)}
                            aria-label="Create a targeted campaign for this aged model"
                            title="Create a targeted campaign for customers interested in this aged model"
                            style={{ padding: '2px 8px' }}
                          >📣</button>
                        )}
                        <button className="btn small" onClick={() => openEdit(v)}>Edit</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
          }

          {/* Pagination footer */}
          {!loading && !loadError && totalElements > 0 && (
            <div
              className="pagination"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 8, padding: '10px 14px', borderTop: '1px solid var(--border, #e5e7eb)',
                flexWrap: 'wrap',
              }}
            >
              <div className="text-xs text-muted">
                {`Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, totalElements)} of ${totalElements}`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className="btn small"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page <= 0}
                >← Prev</button>
                <span className="text-xs" style={{ minWidth: 90, textAlign: 'center' }}>
                  {`Page ${page + 1} of ${Math.max(1, totalPages)}`}
                </span>
                <button
                  className="btn small"
                  onClick={() => setPage(p => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                >Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {detailVehicle && (
        <VehicleDetailModal
          vehicle={detailVehicle}
          onClose={() => setDetailVehicle(null)}
          onEdit={() => { openEdit(detailVehicle); setDetailVehicle(null); }}
        />
      )}

      <VehicleModal
        open={modalOpen}
        mode={modalMode}
        vehicle={activeVehicle}
        onClose={() => setModalOpen(false)}
        onSave={onSave}
      />

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import inventory"
        columns={VEHICLE_IMPORT_COLUMNS}
        sampleRow={VEHICLE_SAMPLE_ROW}
        templateName="inventory-template.csv"
        onImport={(file) => vehiclesService.importFile(file)}
        onDone={() => { setPage(0); load(); loadFacets(); }}
      />
    </>
  );
}

/**
 * Read-only vehicle detail: exact identity (VIN, stock number, rego), arrival
 * date, and — when the car is reserved — the quote holding it. The table only
 * shows the stock number, so two similar cars could not be told apart.
 */
function VehicleDetailModal({ vehicle: v, onClose, onEdit }) {
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (!v.reservedQuoteId) return undefined;
    let cancelled = false;
    quotesService.get(v.reservedQuoteId)
      .then((q) => { if (!cancelled) setQuote(q); })
      .catch(() => { /* reservation shown without the quote details */ });
    return () => { cancelled = true; };
  }, [v.reservedQuoteId]);

  const row = (label, value) => (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 8 }}>
      <div className="card-label">{label}</div>
      <div className="mono text-xs" style={{ wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );
  const title = [v.year, v.make, v.model, v.trim].filter(Boolean).join(' ');

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ width: 'min(560px, 94vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title || 'Vehicle'}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {row('Stock #', v.dmsStockId)}
          {row('VIN', v.vin)}
          {row('Status', STATUS_LABEL[v.stockStatus] || v.stockStatus)}
          {row('Condition', v.condition)}
          {row('Body / fuel / trans.', [v.body, v.fuel, v.transmission].filter(Boolean).join(' · '))}
          {row('Colour', [v.colourExterior, v.colourInterior].filter(Boolean).join(' / '))}
          {row('Odometer', fmtKm(v.km))}
          {row('Driveaway price', fmtPriceFromCents(v.driveAwayPriceCents))}
          {row('Registration', v.rego ? `${v.rego}${v.regoExpiry ? ` (expires ${v.regoExpiry})` : ''}` : null)}
          {row('Build / compliance', [v.buildDate, v.complianceDate].filter(Boolean).join(' / '))}
          {row('Arrived in stock', v.acquiredAt ? formatDateTime(v.acquiredAt) : null)}
          {row('Days in stock', v.stockStatus === 'SOLD' ? null : fmtDays(v.daysInStock))}
          {row(
            'Reservation',
            v.reservedQuoteId
              ? `Quote ${quote?.quoteNumber || '…'}${quote?.status ? ` (${quote.status})` : ''}${v.reservedUntil ? ` · held until ${formatDateTime(v.reservedUntil)}` : ''}`
              : (v.stockStatus === 'RESERVED' ? 'Held manually — no quote attached' : null),
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onEdit}>Edit</button>
          <button type="button" className="btn small primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const color =
    tone === 'success' ? 'var(--success)' :
      tone === 'warning' ? 'var(--warning)' :
        tone === 'danger' ? 'var(--danger)' :
          'var(--text-primary)';
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div className="card-label">{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
